import { inject, injectable } from 'inversify';
import { ArmyMapper } from '@Mapper/ninthAge/ArmyMapper';
import { IMapper } from '@Mapper/IMapper';
import { TYPES } from '@Types';
import { RequestAction, RequestFailure } from '../RequestAction';
import { Army, Nullable } from '@Models';
import CacheRepository from '../CacheRepository';
import {
  RequestArmiesSuccess,
  RequestArmySuccess,
  RequestArmyVersioningsSuccess,
} from './response';
import { army_full, army, ApiClient } from '@Api';

export const unique = (
  value: Army,
  index: number,
  self: Array<Army>,
): boolean => {
  return self.findIndex((i) => i?.id === value?.id) === index;
};

@injectable()
export class ArmyRepository extends CacheRepository<
  number,
  army_full,
  army,
  Army
> {
  @inject(TYPES.Mapper.ArmyMapper) private _armyMapper!: ArmyMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;

  constructor() {
    super({ key: '/ninth_age/armies' });
  }

  public async getArmies({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const armiesDto = await this._apiClient.ninthAge.getArmies({
        locale: locale ?? this._locale.getlocale(),
      });
      await this.setCachedEntities({ data: armiesDto, locale: locale });
      return new RequestArmiesSuccess({
        payload: armiesDto.map((item) => this._armyMapper.map(item)),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  public async getArmy({
    armyId,
    locale,
  }: {
    armyId: number;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const armyDto = await this._apiClient.ninthAge.getArmy({
        locale: locale ?? this._locale.getlocale(),
        armyId: armyId,
      });
      await this.setCachedEntity({ data: armyDto, locale: locale });
      return new RequestArmySuccess({
        payload: this._armyMapper.mapFull(armyDto),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  public async getVersioning({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const itemsDto = await this._apiClient.ninthAge.getUpdatedAtArmies({
        locale: locale ?? this._locale.getlocale(),
      });
      return new RequestArmyVersioningsSuccess({
        payload: itemsDto.map((item) => this._armyMapper.mapVersioning(item)),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  protected getMapper(): IMapper<number, army_full, army, Army> {
    return this._armyMapper;
  }
}
